// 1.45.0.0 (demande du club) : preferences du theme "Lagon", en essai pour les administrateurs.
// 1.45.2.0 : mode clair / sombre / comme l'appareil.
// 1.46.0.0 (demande du club "garder plusieurs themes, chaque adherent choisira celui qui lui
// convient le mieux") : registre de themes ouvert a tous les comptes - Classique, Lagon, Balise.
// Le choix est memorise sur le compte (PUT /auth/me/affichage) pour suivre la personne d'un
// appareil a l'autre ; ce navigateur en garde une copie pour s'afficher tout de suite, y compris
// avant la connexion. Un evenement garde d'accord le menu du compte et les pages de reglage.
import React from 'react';

export const THEMES = [
  { valeur: 'classique', libelle: 'Classique', description: 'Le thème d’origine de l’espace adhérent.', navigation: 'laterale' },
  { valeur: 'lagon', libelle: 'Lagon', description: 'Lecture facilitée, police très lisible, contrastes renforcés.', navigation: 'laterale' },
  { valeur: 'balise', libelle: 'Balise', description: 'Inspiré des cartes marines : navigation en haut, tableaux plus larges, statuts en symboles.', navigation: 'haute' },
  // 1.59.0.0 (demande du club : "ajouter aux themes React Bits") : Balise + palette de grand fond
  // et fond anime (coupe sur telephone et si l'appareil demande de reduire les animations).
  { valeur: 'abysses', libelle: 'Abysses', description: 'Comme Balise, avec un fond animé de grand fond (vagues de lumière, bulles) ; fond fixe sur téléphone.', navigation: 'haute' },
];
export const MODES = [
  { valeur: 'clair', libelle: 'Clair' },
  { valeur: 'sombre', libelle: 'Sombre' },
  { valeur: 'appareil', libelle: 'Comme l’appareil' },
];

const NOMS_THEMES = THEMES.map(t => t.valeur);
const NOMS_MODES = MODES.map(m => m.valeur);
const CLES = { theme: 'cesam_theme', mode: 'cesam_theme_mode', grand: 'cesam_theme_grand' };
// Cles d'avant 1.46.0.0 (Lagon seul), relues une fois pour ne pas perdre le choix deja fait.
const ANCIENNES = { lagon: 'cesam_theme_lagon', mode: 'cesam_theme_lagon_mode', grand: 'cesam_theme_lagon_grand' };
const EVENEMENT = 'cesam-theme';

const lire = (cle) => {
  try { return localStorage.getItem(cle); } catch { return null; }
};
const ecrire = (cle, valeur) => {
  try { localStorage.setItem(cle, valeur); } catch { /* stockage indisponible */ }
};

export const normaliserPreferences = (p) => ({
  theme: NOMS_THEMES.includes(p?.theme) ? p.theme : 'classique',
  mode: NOMS_MODES.includes(p?.mode) ? p.mode : 'clair',
  grand: !!p?.grand,
});

export const lirePreferencesTheme = () => {
  const theme = lire(CLES.theme);
  if (theme) {
    return normaliserPreferences({ theme, mode: lire(CLES.mode), grand: lire(CLES.grand) === '1' });
  }
  return normaliserPreferences({
    theme: lire(ANCIENNES.lagon) === '1' ? 'lagon' : 'classique',
    mode: lire(ANCIENNES.mode),
    grand: lire(ANCIENNES.grand) === '1',
  });
};

const memoriserLocalement = (prefs) => {
  ecrire(CLES.theme, prefs.theme);
  ecrire(CLES.mode, prefs.mode);
  ecrire(CLES.grand, prefs.grand ? '1' : '0');
  window.dispatchEvent(new Event(EVENEMENT));
};

/** Applique les preferences lues sur le compte (connexion, autre appareil) sans les renvoyer. */
export const appliquerPreferencesDuCompte = (depuisCompte) => {
  if (!depuisCompte) return;
  const prefs = normaliserPreferences(depuisCompte);
  const actuelles = lirePreferencesTheme();
  if (prefs.theme !== actuelles.theme || prefs.mode !== actuelles.mode || prefs.grand !== actuelles.grand) {
    memoriserLocalement(prefs);
  }
};

/**
 * Change une ou plusieurs preferences ({ theme, mode, grand }) : effet immediat dans ce navigateur,
 * puis enregistrement sur le compte quand axiosInstance est fourni. Un echec d'enregistrement ne
 * defait pas l'affichage : le choix vaut au moins pour cet appareil.
 */
export const changerPreferencesTheme = (modifs, axiosInstance) => {
  const prefs = normaliserPreferences({ ...lirePreferencesTheme(), ...modifs });
  memoriserLocalement(prefs);
  if (axiosInstance) axiosInstance.put('/auth/me/affichage', prefs).catch(() => {});
  return prefs;
};

export const usePreferencesTheme = () => {
  const [prefs, setPrefs] = React.useState(lirePreferencesTheme);
  React.useEffect(() => {
    const relire = () => setPrefs(lirePreferencesTheme());
    window.addEventListener(EVENEMENT, relire);
    window.addEventListener('storage', relire);
    return () => {
      window.removeEventListener(EVENEMENT, relire);
      window.removeEventListener('storage', relire);
    };
  }, []);
  return prefs;
};

/** Vrai si l'appareil demande un affichage sombre ; suit ses changements. */
export const useAppareilSombre = () => {
  const requete = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;
  const [sombre, setSombre] = React.useState(() => !!requete?.matches);
  React.useEffect(() => {
    if (!requete) return undefined;
    const suivre = (e) => setSombre(e.matches);
    requete.addEventListener('change', suivre);
    return () => requete.removeEventListener('change', suivre);
  }, []);
  return sombre;
};

/**
 * Pose sur <html> les classes du theme choisi : theme-<nom> (sauf Classique), theme-grand
 * (+ theme-lagon-grand pour la feuille Lagon d'origine) et dark. Retirees au demontage.
 */
export const useClassesTheme = (actif = true) => {
  const { theme, mode, grand } = usePreferencesTheme();
  const appareilSombre = useAppareilSombre();
  React.useEffect(() => {
    const racine = document.documentElement.classList;
    const toutes = ['theme-lagon', 'theme-balise', 'theme-abysses', 'theme-grand', 'theme-lagon-grand', 'dark'];
    racine.remove(...toutes);
    if (actif) {
      if (theme !== 'classique') racine.add(`theme-${theme}`);
      // Abysses reprend toute la mise en page de Balise et n'en change que l'habillage.
      if (theme === 'abysses') racine.add('theme-balise');
      if (grand) racine.add('theme-grand');
      if (grand && theme === 'lagon') racine.add('theme-lagon-grand');
      // .dark active aussi les variantes dark: deja presentes dans les composants.
      if (mode === 'sombre' || (mode === 'appareil' && appareilSombre)) racine.add('dark');
    }
    return () => racine.remove(...toutes);
  }, [actif, theme, mode, grand, appareilSombre]);
  return { theme, mode, grand };
};
